import { useEffect, useRef, useState } from "react";
import { requestOtp, verifyOtp } from "../api.js";
import { clearOtpStep, readOtpStep, writeOtpStep } from "../otpResume.js";
import { ArrowRight, Notice } from "./ui.js";

/**
 * Sign in — the same OTP machinery as the form's identity gate, without a form behind it.
 *
 * Both steps are real `<form>` elements rather than a button with an `onKeyDown` handler. That is
 * what gives Enter its behaviour for free, tells a mobile keyboard to show "Go" instead of a
 * newline, and lets a password manager recognise the email field at all.
 */
export const SignInCard = ({
  onSignedIn,
}: {
  onSignedIn: (email: string) => void;
}): JSX.Element => {
  // Same reason as the form's identity gate: reading the code means leaving this page, and a
  // phone that evicts the tab must not cost one of the three code requests the throttle allows.
  const resumed = useRef(readOtpStep("portal"));
  const [step, setStep] = useState<"email" | "code">(resumed.current ? "code" : "email");
  const [email, setEmail] = useState(resumed.current?.email ?? "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  const codeInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === "code") codeInput.current?.focus();
  }, [step]);

  const send = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      // No formSlug: signing in to the portal is not an application to anything.
      const result = await requestOtp(undefined, email);
      writeOtpStep("portal", email);
      setDevCode(result.devOnlyCode ?? null);
      setStep("code");
      // The code field is the only thing to do next, so put the cursor in it rather than making
      // someone on a phone find and tap a box that just appeared.
      window.setTimeout(() => codeInput.current?.focus(), 0);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not send a code.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await verifyOtp(email, code);
      clearOtpStep("portal");
      onSignedIn(email);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That code was not accepted.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sga-card p-6 sm:p-7">
      <h2 className="font-heading text-xl font-semibold" style={{ color: "var(--sga-primary)" }}>
        {step === "email" ? "Sign in" : "Check your email"}
      </h2>

      {step === "email" ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <p className="sga-muted mt-2 text-sm">
            Use the email address you applied with. We send a six-digit code — there is no password
            to remember.
          </p>

          <label htmlFor="portal-email" className="sga-label mt-5">
            Email address
          </label>
          <input
            id="portal-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            required
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-describedby={error ? "portal-signin-error" : undefined}
            aria-invalid={error ? true : undefined}
            className="sga-input mt-1.5"
          />

          <button
            type="submit"
            disabled={!email.includes("@") || busy}
            className="sga-btn sga-btn--primary mt-4 w-full"
          >
            {busy ? "Sending…" : "Send me a code"}
            {!busy && <ArrowRight />}
          </button>
        </form>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void verify();
          }}
        >
          <p className="sga-muted mt-2 text-sm">
            We sent a code to <strong className="font-semibold text-current">{email}</strong>. It
            expires in 10 minutes.
          </p>

          {devCode && (
            <p className="sga-notice mt-3">
              Development only — your code is <strong>{devCode}</strong>
            </p>
          )}

          <label htmlFor="portal-code" className="sga-label mt-5">
            Six-digit code
          </label>
          <input
            id="portal-code"
            ref={codeInput}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
            aria-describedby={error ? "portal-signin-error" : undefined}
            aria-invalid={error ? true : undefined}
            className="sga-input sga-code mt-1.5"
          />

          <button
            type="submit"
            disabled={code.length !== 6 || busy}
            className="sga-btn sga-btn--primary mt-4 w-full"
          >
            {busy ? "Checking…" : "Sign in"}
          </button>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              className="sga-link"
              onClick={() => {
                setStep("email");
                setCode("");
                setError(null);
              }}
            >
              Use a different email address
            </button>
            <button type="button" className="sga-link" disabled={busy} onClick={() => void send()}>
              Send another code
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="mt-4" id="portal-signin-error">
          <Notice tone="error">{error}</Notice>
        </div>
      )}
    </div>
  );
};
